import { postOrgPlaybooksEditSessionsMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type {
  ApiOnboardingGraph,
  EditPlaybookHistory,
  EditPlaybookResponse,
  PlaybookEditSessionNodeError,
  PlaybookSettings,
  PlaybookTemplateKind,
  PlaybookVersion,
  PublicTrace,
  PublicTraceEntry,
  TypedOnboardingData,
} from '@onefootprint/request-types/dashboard';
import { useMutation } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { useCanEdit } from '@/features/playbook-edit';
import type { Banner } from '../components/error-warning-list/error-warning-list';
import { requiredPrerequisiteIds } from '../utils/prerequisites';

const flattenBanners = (items: PlaybookEditSessionNodeError[], type: Banner['type']): Banner[] =>
  items.flatMap(item => item.details.map(detail => ({ nodeId: item.id, description: detail, type })));

export type Data = {
  obcId: string | undefined;
  playbookId: string | undefined;
  canEdit: boolean;
  graph: ApiOnboardingGraph;
  sessionId: string | undefined;
  /** The session's playbook is an unpublished, from-scratch draft (hidden, no committed version). */
  isDraft: boolean;
  /** Versioned playbook key; used to launch sandbox test onboardings for a draft (which has no committed version). */
  key: string | undefined;
  settings: PlaybookSettings;
  /**
   * Banners shown at the top of every node's detail drawer (filtered by
   * nodeId) and in a floating popover on the playbook details page. Includes
   * `error`, `warning`, and `info` variants; the popover appears whenever any
   * banner is present.
   */
  banners: Banner[];
  history: EditPlaybookHistory;
  /** When set, highlight the provided nodes (and their edges) with the provided color */
  nodeHighlight?: NodeHighlight;
  /** When set, the execution trace of an onboarding through the graph that we want to highlight */
  traceEntries?: PublicTraceEntry[];
  /** When set, the workflow ID associated with the `traceEntries` */
  traceWorkflowId?: string;
  /** IDs of branch/conditional nodes whose branches are collapsed */
  collapsedNodeIds: string[];
  /**
   * Prerequisites required by the version this edit started from, so the prerequisite drawer can flag
   * the ones this edit adds. Undefined until that version loads, which must not read as "none".
   */
  baseRequiredPrerequisites?: string[];
  /** The same baseline for outputs, letting the output drawer flag the ones this edit breaks. */
  baseOutputs?: TypedOnboardingData[];
  /**
   * Nodes added this session whose form the user hasn't saved yet. A new node is created empty, so the
   * backend flags it on the add itself; `visibleBanners` hides those until a node or playbook save.
   */
  pristineNodeIds: string[];
  /**
   * A node to bring into view. Only the panel knows its own final width, and centring before it opens
   * leaves the node behind it, so the panel consumes this and pans once.
   */
  focusNodeId?: string;
};

type NodeHighlight = {
  color: 'error' | 'info';
  ids: string[];
  /** Track which node set this highlight */
  nodeId?: string;
  variant?: 'border-and-background';
};

type PlaybookBuilderStore = {
  data: Data;
  set: (data: Partial<Data>) => void;
};

const initialState: Data = {
  // An OBC ID may not be defined if the onboarding was through an ad hoc workflow, or if we're viewing an execution trace.
  obcId: undefined,
  // Session ID may be undefined if we're not in edit mode.
  sessionId: undefined,
  isDraft: false,
  key: undefined,
  playbookId: undefined,
  graph: {
    id: '',
    nodes: [],
    subject: 'person',
    availableOperations: [],
  },
  settings: {
    kind: 'kyc',
    name: '',
    promptForPasskey: false,
    requiredAuthMethods: [],
    requiresAuthentication: false,
    supportInternalMatching: false,
    isModern: false,
  },
  canEdit: false,
  history: { hasChanges: false },
  banners: [],
  traceEntries: undefined,
  traceWorkflowId: undefined,
  collapsedNodeIds: [],
  baseRequiredPrerequisites: undefined,
  baseOutputs: undefined,
  pristineNodeIds: [],
  focusNodeId: undefined,
};

export const usePlaybookBuilderStore = create<PlaybookBuilderStore>()(zustandSet => ({
  data: initialState,
  set: (partialData: Partial<Data>) =>
    zustandSet(state => ({
      data: { ...state.data, ...partialData },
    })),
}));

// Outside the hook so a caller can reveal without subscribing to the whole store.
export const revealNodeErrors = () =>
  usePlaybookBuilderStore.setState(state => ({ data: { ...state.data, pristineNodeIds: [] } }));

// Provider component for testing - allows setting initial store state
export const PlaybookBuilderStoreProvider = ({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData?: Partial<Data>;
}) => {
  useEffect(() => {
    if (initialData) {
      usePlaybookBuilderStore.getState().set(initialData);
    }
  }, [initialData]);

  return <>{children}</>;
};

type ResetToPlaybookVersion = {
  obc: PlaybookVersion;
};
type ResetToPublicTrace = {
  trace: PublicTrace;
};
type ResetToGraph = {
  graph: ApiOnboardingGraph;
};

// A graph may be shown for either a playbook/OBC or for an execution trace.
// Since graphs can be executed without playbooks/OBCs (e.g. ad hoc document
// collection), we must be able to reference the graph in each of these
// contexts. This is slightly confusing since the component is called
// PlaybookBuilder even for execution trace viewing.
type ResetStateInput = ResetToPlaybookVersion | ResetToPublicTrace | ResetToGraph;

const useStore = () => {
  const errorToast = useRequestErrorToast();
  const { data, set } = usePlaybookBuilderStore();
  const editMutation = useMutation(postOrgPlaybooksEditSessionsMutation());
  const canEdit = useCanEdit();
  const { t } = useTranslation('playbook-builder');

  const reset = (input?: ResetStateInput) => {
    if (input && 'obc' in input) {
      set({
        ...initialState,
        obcId: input.obc.id,
        playbookId: input.obc.playbookId,
        graph: input.obc.graph,
        baseRequiredPrerequisites: requiredPrerequisiteIds(input.obc.graph),
        baseOutputs: input.obc.outputs,
        canEdit: canEdit(input.obc),
        settings: input.obc.settings ?? initialState.settings,
      });
    } else if (input && 'trace' in input) {
      set({
        ...initialState,
        graph: input.trace.graph,
        traceEntries: input.trace.entries,
        traceWorkflowId: input.trace.workflowId,
      });
    } else if (input && 'graph' in input) {
      set({ ...initialState, graph: input.graph, canEdit: false });
    } else {
      set({ ...initialState });
    }
  };

  const startCreating = async ({ template, name }: { template: PlaybookTemplateKind; name: string }) => {
    set({ ...initialState, settings: { ...initialState.settings, name } });
    try {
      const response = await editMutation.mutateAsync({
        body: { kind: 'create', template, name },
      });
      setGraphUpdate(response, { canEdit: true });
    } catch (error) {
      errorToast(error);
      reset();
      throw error;
    }
  };

  const getOrCreateEditSession = async () => {
    const current = usePlaybookBuilderStore.getState().data;
    if (current.sessionId) {
      return { id: current.sessionId, graphId: current.graph.id };
    }
    if (!current.obcId) {
      throw new Error('Trying to start an edit session when no playbook version is available');
    }
    const playbookVersionId = current.obcId;
    const response = await editMutation.mutateAsync({
      body: { kind: 'edit', playbookVersionId },
    });
    setGraphUpdate(response, { canEdit: true });
    return { id: response.id, graphId: response.graph.id };
  };

  const setGraphUpdate = (
    response: EditPlaybookResponse,
    extra?: { canEdit?: boolean; baseRequiredPrerequisites?: string[]; baseOutputs?: TypedOnboardingData[] },
  ) => {
    const banners: Banner[] = [
      ...flattenBanners(response.errors, 'error'),
      ...flattenBanners(response.warnings, 'warning'),
      ...flattenBanners(response.info, 'info'),
    ];
    set({
      obcId: response.versionId,
      playbookId: response.playbookId,
      sessionId: response.id,
      isDraft: response.isDraft,
      key: response.key,
      graph: response.graph,
      banners,
      history: response.history,
      settings: response.settings,
      traceEntries: response.testEntries,
      // A draft has no committed version to fetch, so `reset` never runs to derive `canEdit` for it.
      ...(response.isDraft ? { canEdit: canEdit() } : {}),
      ...extra,
    });
  };

  const setNodeHighlight = (
    nodeHighlight?: NodeHighlight | ((prev: NodeHighlight | undefined) => NodeHighlight | undefined),
  ) => {
    usePlaybookBuilderStore.setState(state => ({
      data: {
        ...state.data,
        nodeHighlight: typeof nodeHighlight === 'function' ? nodeHighlight(state.data.nodeHighlight) : nodeHighlight,
      },
    }));
  };

  const setPristineNodeIds = (update: (ids: string[]) => string[]) => {
    usePlaybookBuilderStore.setState(state => ({
      data: { ...state.data, pristineNodeIds: update(state.data.pristineNodeIds) },
    }));
  };
  const markNodePristine = (nodeId: string) => setPristineNodeIds(ids => [...ids, nodeId]);
  const markNodeTouched = (nodeId: string) => setPristineNodeIds(ids => ids.filter(id => id !== nodeId));
  const setFocusNodeId = (focusNodeId: string | undefined) => {
    usePlaybookBuilderStore.setState(state => ({ data: { ...state.data, focusNodeId } }));
  };
  const requestNodeFocus = (nodeId: string) => setFocusNodeId(nodeId);
  const clearNodeFocus = () => setFocusNodeId(undefined);

  // Only errors are held back: nothing reveals a new node's warnings or info later, so those stay visible.
  const visibleBanners = data.banners.filter(
    banner => banner.type !== 'error' || !data.pristineNodeIds.includes(banner.nodeId),
  );

  const executionTraceBanners = (data.traceEntries ?? []).flatMap((entry): Banner[] => {
    if (entry.result.signalKind === 'error') {
      const description = {
        message: entry.result.message
          ? t($ => $.errorList.nodeFailedExecutionWithMessage, { message: entry.result.message })
          : t($ => $.errorList.nodeFailedExecution),
        ...(entry.result.internalOnly ? { context: { internalOnly: true } } : {}),
      };
      return [{ nodeId: entry.decisionNodeId, description, type: 'error' as const }];
    }
    return [];
  });
  const latestTrace = data.traceEntries?.findLast(() => true);
  if (latestTrace?.result.signalKind === 'yield' && latestTrace.result.yieldKind === 'dependencies_not_satisfied') {
    // The latest node's execution is waiting for an unsatisfied dependencies. This happens when an asynchronous node (like AI decision) outputs a piece of data required to enter this node.
    // Add warning annotations to the nodes that are being retried in the background.
    for (const entry of data.traceEntries ?? []) {
      if (entry.result.signalKind === 'retryable_error_proceed') {
        // Only for employees for now, show when a node was put into `retryable_error_proceed`.
        // We should eventually show this to end users too, but we currently don't overwrite the `OnboardingTraceEntry`
        // when the node _is_ retried, so we have no way of differentiating whether the node has been successfully re-run.
        const description = { message: t($ => $.errorList.nodeExecutingAsync) };
        executionTraceBanners.push({ nodeId: entry.decisionNodeId, description, type: 'warning' as const });
        // NOTE: this may show some false positives... best thing to do would be to add a new trace entry when a retryable node is successfully executed
      }
    }
    executionTraceBanners.push({
      nodeId: latestTrace.decisionNodeId,
      description: { message: 'Waiting for dependencies to be satisfied' },
      type: 'warning' as const,
    });
  }
  for (const entry of data.traceEntries ?? []) {
    for (const banner of entry.banners ?? []) {
      executionTraceBanners.push({
        nodeId: entry.decisionNodeId,
        description: { message: banner.message },
        type: banner.severity,
      });
    }
  }

  return {
    data,
    visibleBanners,
    executionTraceBanners,
    markNodePristine,
    markNodeTouched,
    revealNodeErrors,
    requestNodeFocus,
    clearNodeFocus,
    set,
    setGraphUpdate,
    reset,
    getOrCreateEditSession,
    startCreating,
    /** Set the delete operation that's being hovered, if any. This allows us to highlight nodes that will be deleted */
    setNodeHighlight,
  };
};

export default useStore;
export { initialState };
